// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/system-architecture.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import {
  CloudUpload,
  HourglassEmpty,
  BarChart,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import VoiceUploadModal from './VoiceUploadModal';
import ProcessedFilesTable from './ProcessedFilesTable';
import DragDropZone from './common/DragDropZone';
import PageSectionHeader from './common/PageSectionHeader';
import './VoiceUpload.css';

const VoiceUpload = () => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preSelectedFiles, setPreSelectedFiles] = useState([]);
  const [queueStatus, setQueueStatus] = useState({
    waiting: [],           // Step 1: queued
    processing: [],        // Step 2: processing  
    ready_to_analyse: [],  // Step 3: ready_to_analyse
    analysing: [],         // Step 4: analysing
    completed: [],         // Step 5: completed
    failed: [],            // Step 6: failed
    cancelled: 0,
    uploading: 0,
    total_files: 0,
  });

  // Fetch queue status
  const fetchQueueStatus = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.VOICE_STATUS);
      const data = response.data;
      
      // Map backend status to frontend format
      setQueueStatus({
        waiting: data.status_counts?.queued || 0,
        processing: data.status_counts?.processing || 0,
        ready_to_analyse: data.status_counts?.ready_to_analyse || 0,
        analysing: data.status_counts?.analysing || 0,
        completed: data.status_counts?.completed || 0,
        failed: data.status_counts?.failed || 0,
        cancelled: data.status_counts?.cancelled || 0,
        uploading: data.status_counts?.uploading || 0,
        total_files: data.total_files || 0,
        estimated_wait_time: data.estimated_wait_time_minutes || 0
      });
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  };

  // Real-time queue updates
  useEffect(() => {
    const interval = setInterval(fetchQueueStatus, 5000); // Update every 5 seconds
    fetchQueueStatus(); // Initial fetch
    
    return () => clearInterval(interval);
  }, []);

  // Handle file upload from modal (now receives individual upload results)
  const handleUpload = async (uploadResult) => {
    setUploading(true);
    try {
      // Process upload result from modal
      console.log('Upload result:', uploadResult);
      
      // Show success notification
      if (uploadResult.file_id) {
        console.log(`✅ File uploaded successfully: ${uploadResult.file_name} (ID: ${uploadResult.file_id})`);
        console.log(`📋 Status: ${uploadResult.status}`);
        console.log(`🌐 Language: ${uploadResult.language}`);
        console.log(`💬 Message: ${uploadResult.message}`);
      }
      
      // Refresh queue status after upload
      await fetchQueueStatus();
      
    } catch (error) {
      console.error('Upload processing error:', error);
      // Show error notification
      console.error(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };


  // Auto-refresh queue status
  useEffect(() => {
    fetchQueueStatus();
    const interval = setInterval(fetchQueueStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: 'auto', 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%',
      overflow: 'visible',
      flex: 1
    }}>
      <PageSectionHeader
        icon={<CloudUpload />}
        title="Voice Upload & Processing"
        description="Upload audio files for transcription, analysis, and QA scoring"
      />

      {/* File Upload Section with Drag & Drop */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Voice Files
          </Typography>
          
          <DragDropZone
            onFilesSelected={(files) => {
              setPreSelectedFiles(files);
              setUploadModalOpen(true);
            }}
            accept="audio/*"
            multiple={true}
            maxFiles={5}
            maxSize={50}
            disabled={uploading}
            sx={{ mb: 2 }}
          >
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CloudUpload 
                sx={{ 
                  fontSize: 40, 
                  color: 'primary.main', 
                  mb: 1,
                  transition: 'transform 0.2s ease-in-out',
                }} 
              />
              <Typography variant="h6" gutterBottom>
                Drag & Drop Voice Files
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                or click to browse files
              </Typography>
              <Button
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadModalOpen(true);
                }}
                disabled={uploading}
                startIcon={<CloudUpload />}
                size="small"
              >
                Select Files
              </Button>
            </Box>
          </DragDropZone>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Supported formats: WAV, MP3, M4A, FLAC, OGG • Maximum 5 files, 50MB per file
          </Typography>

          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {/* Voice Processing Flow Section */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1
        }}>
          <BarChart sx={{ 
            fontSize: '1.8rem',
            background: 'linear-gradient(45deg, #4caf50, #f44336, #2196f3)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 4px rgba(33,150,243,0.3))'
          }} />
          <Typography variant="h6" sx={{ 
            color: '#1976d2', 
            fontWeight: 600,
            fontSize: '20px'
          }}>
            Voice Processing Flow
          </Typography>
        </Box>
      </Box>

      {/* Status Cards Container - 16% Width Each */}
      <Box sx={{ 
        width: '100%',
        py: 2
      }}>
        <Box sx={{ 
          display: 'flex',
          gap: 1,
          width: '100%',
          flexWrap: 'nowrap'
        }}>
              {/* Waiting Queue - Step 1 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #ff9800', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="1" color="warning" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    ⏸️ Waiting ({queueStatus.waiting || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.waiting > 0 
                      ? `${queueStatus.waiting} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>

              {/* Processing Files - Step 2 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #2196f3', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="2" color="primary" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    🔄 Processing ({queueStatus.processing || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.processing > 0 
                      ? `${queueStatus.processing} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>

              {/* Ready for Analysis - Step 3 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #2196f3', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="3" color="info" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    🔍 Ready ({queueStatus.ready_to_analyse || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.ready_to_analyse > 0 
                      ? `${queueStatus.ready_to_analyse} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>

              {/* Analysing - Step 4 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #9c27b0', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="4" color="secondary" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    🧠 Analysing ({queueStatus.analysing || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.analysing > 0 
                      ? `${queueStatus.analysing} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>

              {/* Completed Files - Step 5 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #4caf50', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="5" color="success" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    ✅ Complete ({queueStatus.completed || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.completed > 0 
                      ? `${queueStatus.completed} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>

              {/* Failed Files - Step 6 */}
              <Box sx={{ width: '16%', flexShrink: 0 }}>
                <Paper sx={{ 
                  p: 2, 
                  border: '2px solid #f44336', 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '120px'
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '14px', textAlign: 'center' }}>
                    <Chip label="6" color="error" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    ❌ Failed ({queueStatus.failed || 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', textAlign: 'center' }}>
                    {queueStatus.failed > 0 
                      ? `${queueStatus.failed} files` 
                      : "No files"
                    }
                  </Typography>
                </Paper>
              </Box>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info">
          Total files (all statuses): {queueStatus.total_files}
          {(queueStatus.cancelled > 0 || queueStatus.uploading > 0) && (
            <>
              {' '}
              — Cancelled: {queueStatus.cancelled}
              {queueStatus.uploading > 0 ? ` · Uploading: ${queueStatus.uploading}` : ''}
            </>
          )}
          {' '}| Estimated wait: {queueStatus.estimated_wait_time} min
          {(queueStatus.cancelled > 0 || queueStatus.uploading > 0) && (
            <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
              Cancelled/uploading are excluded from the six pipeline boxes; they do not occupy a process-worker slot or block queued jobs.
            </Typography>
          )}
        </Alert>
      </Box>

      {/* Processed Files Table */}
      <Box sx={{ flex: 1 }}>
        <ProcessedFilesTable />
      </Box>

      {/* Upload Modal */}
      <VoiceUploadModal
        open={uploadModalOpen}
        onClose={() => {
          setPreSelectedFiles([]);
          setUploadModalOpen(false);
        }}
        onUpload={handleUpload}
        preSelectedFiles={preSelectedFiles}
      />
    </Box>
  );
};

export default VoiceUpload;
